import React from 'react';
import { CheckCircle, Cancel, Error as ErrorIcon, HourglassBottom, HelpOutline } from '@mui/icons-material';
import { OrderV2, PreOrderCountDetailsDto } from '@kotipizzagroup/kotipizza-orders-api-client';
import {
  EdenredMerchantStatus,
  KlarnaMerchantStatus,
  MerchantDto,
  PaymentMethodSettings,
} from '@kotipizzagroup/kotipizza-payments-api-admin-client';
import {
  GetOpeningHoursDto,
  GetOpeningHoursExceptionDto,
  OpeningHourTypeEnum,
  RestaurantStatusDto,
  RestaurantStatusEnum,
  ShopOpeningHoursDto,
  WebshopShopDto,
  WeekdayEnum,
} from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { AdyenMethodType, getMethodLabel } from '../SingleRestaurantPage/tabs/Payments/components/adyen/utils';
import dayjs from '../../shared/utils/dayJsCustomParseFormat';
import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';

// Opening-hour type ids as stored on ShopOpeningHoursDto. Mirrors the constants used on the
// restaurants overview (RestaurantsPage/tabs/Info.tsx).
export const DELIVERY_OPENING_HOUR_TYPE_ID = 2;
export const PICKUP_OPENING_HOUR_TYPE_ID = 4;

// A channel whose estimate is at or above this many minutes is highlighted as degraded.
export const SLOW_ESTIMATE_MINUTES = 60;

export const isSlowEstimate = (estimate: number): boolean => estimate >= SLOW_ESTIMATE_MINUTES;

// How often the day's orders are re-fetched while a page showing them is open, and the length of one
// sweep of the dashboard's refresh bar. The overview's cards poll at the same pace, so the two never
// show a different day for the same shop.
export const TODAY_REFETCH_INTERVAL_MS = 60_000;

export type StatusTone = 'open' | 'warning' | 'error' | 'neutral';

export type StatusView = {
  tone: StatusTone;
  labelKey: string;
  icon: React.ReactNode;
};

// Resolves the status the merchant actually sees right now: a temporary status (e.g. "closed until
// 18:00") takes precedence over the permanent one while it is active.
export const getEffectiveStatus = (status?: RestaurantStatusDto): RestaurantStatusEnum | undefined =>
  status?.isTemporaryStatusActive && status.temporaryStatus ? status.temporaryStatus : status?.status;

// The "until HH:mm" tail on a temporary closure, and nothing for any other state: a busy spell has no
// end time worth promising, and a permanent closure has none at all.
export const getStatusDetail = (status: RestaurantStatusDto | undefined, t: TFunction): string | undefined =>
  getEffectiveStatus(status) === RestaurantStatusEnum.TemporarilyClosed && status?.temporaryStatusUntil
    ? t('singleRestaurant.status.until', { time: dayjs(status.temporaryStatusUntil).format('HH:mm') })
    : undefined;

export const getStatusView = (status?: RestaurantStatusEnum): StatusView => {
  switch (status) {
    case RestaurantStatusEnum.Open:
      return { tone: 'open', labelKey: 'singleRestaurant.status.open', icon: <CheckCircle fontSize="inherit" /> };
    case RestaurantStatusEnum.Busy:
      return { tone: 'warning', labelKey: 'singleRestaurant.status.busy', icon: <ErrorIcon fontSize="inherit" /> };
    case RestaurantStatusEnum.DeliveryBusy:
      return {
        tone: 'warning',
        labelKey: 'singleRestaurant.status.deliveryBusy',
        icon: <ErrorIcon fontSize="inherit" />,
      };
    case RestaurantStatusEnum.OpeningSoon:
      return {
        tone: 'neutral',
        labelKey: 'singleRestaurant.status.openingSoon',
        icon: <HourglassBottom fontSize="inherit" />,
      };
    case RestaurantStatusEnum.TemporarilyClosed:
      return {
        tone: 'warning',
        labelKey: 'singleRestaurant.status.temporarilyClosed',
        icon: <ErrorIcon fontSize="inherit" />,
      };
    case RestaurantStatusEnum.PermanentlyClosed:
      return {
        tone: 'error',
        labelKey: 'singleRestaurant.status.permanentlyClosed',
        icon: <Cancel fontSize="inherit" />,
      };
    default:
      return { tone: 'neutral', labelKey: 'singleRestaurant.status.unknown', icon: <HelpOutline fontSize="inherit" /> };
  }
};

export type ChannelState = 'open' | 'restricted' | 'closed';

export type ChannelInfo = {
  // Whether the channel is enabled and, if so, whether today's hours look normal or cut short.
  state: ChannelState;
  // Today's opening hours as "HH:mm – HH:mm", when the channel has hours configured.
  todaysHours?: string;
  // Current ETA in minutes (delivery/pickup only).
  estimate?: number;
};

const expectedClosingTime = '20:30';
const lastClosingTime = '06:00';

const isTimeBefore = (time: string, expected: string): boolean =>
  dayjs(time, 'HH:mm').isBefore(dayjs(expected, 'HH:mm'));

// Sun=0 … Sat=6, matching Date.getDay(); indexes into the day-prefixed fields on ShopOpeningHoursDto.
const dayPrefixes = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const getTodayField = (hours: ShopOpeningHoursDto, suffix: 'StartTime' | 'EndTime'): string => {
  const prefix = dayPrefixes[new Date().getDay()];
  return hours[`${prefix}${suffix}` as keyof ShopOpeningHoursDto] as string;
};

// Undefined when either end of today's hours is missing, so callers render nothing rather than
// a "undefined – undefined" string.
const getTodaysHours = (hours: ShopOpeningHoursDto): string | undefined => {
  const start = getTodayField(hours, 'StartTime');
  const end = getTodayField(hours, 'EndTime');
  return start && end ? `${start} – ${end}` : undefined;
};

// A channel that shuts before 20:30 (but not one running past midnight into the small hours) is
// serving a cut-down day, which both the overview and the dashboard flag as restricted.
const isRestrictedClosing = (closingTime: string): boolean =>
  isTimeBefore(closingTime, expectedClosingTime) && !isTimeBefore(closingTime, lastClosingTime);

// Derives a channel's state the same way the restaurants overview does: disabled webshop or method
// flag means closed, an early closing time (before 20:30 but after 06:00) means restricted.
export const getChannelInfo = (
  restaurant: WebshopShopDto,
  openingHourTypeId: number,
  isMethodEnabled: boolean,
  estimate: number
): ChannelInfo => {
  if (!restaurant.hasWebSale || !isMethodEnabled) {
    return { state: 'closed' };
  }

  const hours = (restaurant.shopOpeningHours ?? []).find((h) => h.openingHourTypeId === openingHourTypeId);
  if (!hours) {
    return { state: 'closed' };
  }

  return {
    state: isRestrictedClosing(getTodayField(hours, 'EndTime')) ? 'restricted' : 'open',
    todaysHours: getTodaysHours(hours),
    estimate,
  };
};

// Sun=0 … Sat=6, matching Date.getDay().
const weekdaysByDayNumber: WeekdayEnum[] = [
  WeekdayEnum.Sunday,
  WeekdayEnum.Monday,
  WeekdayEnum.Tuesday,
  WeekdayEnum.Wednesday,
  WeekdayEnum.Thursday,
  WeekdayEnum.Friday,
  WeekdayEnum.Saturday,
];

const todaysWeekday = (): WeekdayEnum => weekdaysByDayNumber[new Date().getDay()];

// One opening-hours row: the given type's entry for today's weekday. Undefined when the shop has no
// row of that type at all, which callers must render as "not known" rather than as closed.
const findTodaysRow = (
  openingHours: GetOpeningHoursDto[] | undefined,
  type: OpeningHourTypeEnum
): GetOpeningHoursDto | undefined =>
  (openingHours ?? []).find((hours) => hours.type === type && hours.weekday === todaysWeekday());

// Two times as one label, or undefined when either end is missing — so callers render nothing
// rather than "undefined – undefined".
const formatHourRange = (startTime?: string, endTime?: string): string | undefined =>
  startTime && endTime ? `${startTime} – ${endTime}` : undefined;

// Eat-in is not a webshop channel: it has no ETA and is not gated on hasWebSale, and its hours are
// not among the numeric-id rows on the shop (those only carry delivery and pickup). They come from
// the opening-hours endpoint instead, keyed by OpeningHourTypeEnum. Undefined while that query is
// still in flight, so the card shows a placeholder rather than claiming the dining room is closed.
export const getEatInChannelInfo = (
  restaurant: WebshopShopDto,
  openingHours: GetOpeningHoursDto[] | undefined
): ChannelInfo | undefined => {
  if (!restaurant.hasEatin) {
    return { state: 'closed' };
  }
  if (!openingHours) {
    return undefined;
  }

  const today = findTodaysRow(openingHours, OpeningHourTypeEnum.EatIn);
  if (!today || today.isClosed) {
    return { state: 'closed' };
  }

  return {
    state: isRestrictedClosing(today.endTime) ? 'restricted' : 'open',
    todaysHours: formatHourRange(today.startTime, today.endTime),
  };
};

/* --------------------------- restaurant opening hours ---------------------- */

// Which set of hours the header ended up showing. Plenty of shops configure webshop hours and never
// fill in the premises' own, so the chip says which it is rather than passing one off as the other.
export type HoursSource = 'restaurant' | 'webshop';

export type TodaysHours = { source: HoursSource } & (
  | { kind: 'closed' }
  | { kind: 'allDay' }
  | { kind: 'hours'; hours: string }
);

// A day with no edges: midnight to midnight, or simply ending where it starts — a full circle round
// the clock. A *closed* day is stored the same way (the hours form writes 00:00 to 00:00 for one),
// so `isClosed` has to have been ruled out before asking this.
const isAllDay = (startTime: string, endTime: string): boolean =>
  startTime === endTime || (startTime === '00:00' && (endTime === '23:59' || endTime === '24:00'));

// Today's row for one opening-hour type, exceptions applied.
//
// An exception dated today wins over the weekday row, which is the whole reason this is worth
// deriving rather than reading straight off the weekly table: on the days people most need the
// header to be right (a holiday, a one-off early close) the weekday row is exactly wrong. Nothing
// else in the app reconciles the two — the Hours tab lists exceptions separately.
const readTodaysHours = (
  type: OpeningHourTypeEnum,
  source: HoursSource,
  openingHours?: GetOpeningHoursDto[],
  exceptions?: GetOpeningHoursExceptionDto[]
): TodaysHours | undefined => {
  const today = dayjs().startOf('day');
  const exception = (exceptions ?? []).find((entry) => entry.type === type && dayjs(entry.date).isSame(today, 'day'));

  const row = exception ?? findTodaysRow(openingHours, type);
  if (!row) {
    return undefined;
  }
  if (row.isClosed) {
    return { source, kind: 'closed' };
  }
  if (row.startTime && row.endTime && isAllDay(row.startTime, row.endTime)) {
    return { source, kind: 'allDay' };
  }

  const hours = formatHourRange(row.startTime, row.endTime);
  return hours ? { source, kind: 'hours', hours } : undefined;
};

// The premises' own "Ravintola" hours where they exist, falling back to the webshop's — plenty of
// shops only ever configure the latter, and a header that stays empty for most restaurants is worth
// less than one that says which hours it is showing.
//
// A restaurant row that exists and says *closed* is an answer, so it is kept rather than fallen
// through: only a missing row hands over to the webshop.
//
// Undefined when neither is configured, so the header shows no chip rather than inventing hours.
// Note this deliberately avoids Hours/utils.ts `createHoursMap`, which fills a missing row with a
// 10:00–22:00 default and would state opening hours for a shop that has none.
export const getTodaysOpeningHours = (
  openingHours?: GetOpeningHoursDto[],
  exceptions?: GetOpeningHoursExceptionDto[]
): TodaysHours | undefined =>
  readTodaysHours(OpeningHourTypeEnum.Restaurant, 'restaurant', openingHours, exceptions) ??
  readTodaysHours(OpeningHourTypeEnum.Ecommerce, 'webshop', openingHours, exceptions);

/* ------------------------------ hourly orders ------------------------------ */

export type HourWindow = { firstHour: number; lastHour: number };

export type HourlyOrders = { hour: number; value: number }[];

// The channels that can actually take an order; the shop's own (Restaurant) hours are the
// building's, which can open well before anything is sellable.
const SALES_CHANNEL_TYPES: OpeningHourTypeEnum[] = [
  OpeningHourTypeEnum.Ecommerce,
  OpeningHourTypeEnum.Delivery,
  OpeningHourTypeEnum.Pickup,
  OpeningHourTypeEnum.EatIn,
];

const parseHour = (time: string): number => dayjs(time, 'HH:mm').hour();
const parseMinute = (time: string): number => dayjs(time, 'HH:mm').minute();

// The last whole hour an order can still be placed in. A channel closing at 21:00 sharp sells
// nothing during 21, so its last hour is 20; one closing at 21:30 still sells during 21. A channel
// running past midnight reports an end before its start; the day's grid stops at 23.
const lastSellingHour = (startTime: string, endTime: string): number =>
  parseHour(endTime) < parseHour(startTime)
    ? 23
    : parseMinute(endTime) > 0
    ? parseHour(endTime)
    : parseHour(endTime) - 1;

// Today's selling window as whole hours: earliest opening across the sales channels to the last hour
// one of them can still take an order.
//
// Delivery and pickup come off the shop itself, which is already loaded when the page first paints —
// so the window (and therefore the chart's empty day) resolves immediately and survives the
// opening-hours query being slow, failing, or returning rows this filter does not recognise. The
// enum-keyed endpoint only adds what the shop rows cannot carry: eat-in and ecommerce.
//
// Undefined only when nothing at all says when the shop sells today.
export const getTodaysOpenHourWindow = (
  restaurant: WebshopShopDto,
  openingHours?: GetOpeningHoursDto[]
): HourWindow | undefined => {
  const starts: number[] = [];
  const ends: number[] = [];

  const addWindow = (startTime?: string, endTime?: string): void => {
    if (!startTime || !endTime) return;
    starts.push(parseHour(startTime));
    ends.push(lastSellingHour(startTime, endTime));
  };

  const shopChannels: [number, boolean][] = [
    [DELIVERY_OPENING_HOUR_TYPE_ID, restaurant.hasDelivery],
    [PICKUP_OPENING_HOUR_TYPE_ID, restaurant.hasPickup],
  ];
  for (const [openingHourTypeId, isEnabled] of shopChannels) {
    if (!isEnabled) continue;
    const hours = (restaurant.shopOpeningHours ?? []).find((row) => row.openingHourTypeId === openingHourTypeId);
    if (hours) {
      addWindow(getTodayField(hours, 'StartTime'), getTodayField(hours, 'EndTime'));
    }
  }

  // Several types at once, so this one scans rather than going through findTodaysRow.
  const today = todaysWeekday();
  for (const row of openingHours ?? []) {
    if (!SALES_CHANNEL_TYPES.includes(row.type) || row.weekday !== today || row.isClosed) continue;
    addWindow(row.startTime, row.endTime);
  }

  if (starts.length === 0) {
    return undefined;
  }

  const firstHour = Math.min(...starts);
  const lastHour = Math.max(...ends);
  return lastHour < firstHour ? undefined : { firstHour, lastHour };
};

// Orders placed per hour across the opening window, zero-filled so quiet hours stay visible as gaps
// in the bars rather than disappearing. The window is widened to cover any hour that actually has
// orders, so an order placed outside opening hours is never hidden. Empty when there is nothing to
// plot at all (no hours, no orders).
export const getHourlyOrderCounts = (orders: OrderV2[], window?: HourWindow): HourlyOrders => {
  const ordersByHour = new Map<number, number>();
  for (const order of orders) {
    if (!order.orderPlaced) continue;
    const hour = dayjs(order.orderPlaced).hour();
    ordersByHour.set(hour, (ordersByHour.get(hour) ?? 0) + 1);
  }

  const hours = [...ordersByHour.keys()];
  if (window) {
    hours.push(window.firstHour, window.lastHour);
  }
  if (hours.length === 0) {
    return [];
  }

  const firstHour = Math.min(...hours);
  const lastHour = Math.max(...hours);
  return Array.from({ length: lastHour - firstHour + 1 }, (_, index) => ({
    hour: firstHour + index,
    value: ordersByHour.get(firstHour + index) ?? 0,
  }));
};

/* -------------------------------- preorders -------------------------------- */

// How many days the dashboard's preorder strip covers: today plus the rest of the week ahead.
export const PREORDER_WEEK_DAYS = 7;

export type PreorderDay = { date: Dayjs; count: number };

// One of today's preorders waiting on the kitchen. `isLate` marks one whose requested time has
// already passed with nothing started — the row that most wants looking at.
export type UpcomingPreorder = { order: OrderV2; isLate: boolean };

// Today and the six days after it, each carrying that day's preorder count. Days the API says
// nothing about are kept at zero rather than dropped, so the strip is always a full week.
//
// Counts `pizzaProductCount`, which is the number the orders view's own day strip puts in its badge:
// the same day showing two different numbers in two places is worse than the label being loose.
export const getPreorderWeek = (counts?: PreOrderCountDetailsDto[]): PreorderDay[] => {
  const countByDay = new Map<number, number>();
  for (const entry of counts ?? []) {
    countByDay.set(dayjs(entry.date).startOf('day').valueOf(), entry.pizzaProductCount);
  }

  const today = dayjs().startOf('day');
  return Array.from({ length: PREORDER_WEEK_DAYS }, (_, index) => {
    const date = today.add(index, 'day');

    return { date, count: countByDay.get(date.valueOf()) ?? 0 };
  });
};

// Today's preorders the kitchen has not started yet, ordered by how much they want attention:
// overdue ones first (longest overdue at the top), then the rest by how soon they are due.
//
// "Not started" is `RegisterManufacturingTime` being unset — the same field orderStatusFromTimeStamps
// reads to call an order "in the oven" (shared/Orders/utils/orderStatus.ts). Cancelled orders are
// excluded on OrderCancelledTime, the canonical "peruttu" marker used throughout the orders views.
export const getUpcomingPreorders = (orders?: OrderV2[]): UpcomingPreorder[] => {
  const now = dayjs().valueOf();

  return (
    (orders ?? [])
      .filter((order) => order.preOrderTime && !order.OrderCancelledTime && !order.RegisterManufacturingTime)
      // preOrderTime is parsed once per order rather than on every comparison the sort makes.
      .map((order) => ({ order, ts: dayjs(order.preOrderTime).valueOf() }))
      .sort((a, b) => {
        const aLate = a.ts <= now;
        const bLate = b.ts <= now;

        return aLate === bLate ? a.ts - b.ts : Number(bLate) - Number(aLate);
      })
      .map(({ order, ts }) => ({ order, isLate: ts <= now }))
  );
};

/* ----------------------------- payment methods ----------------------------- */

// One readable row per payment method (mirrors the restaurant Payments tab checklist), with
// its enabled state. Adyen (webshop) methods come from the settings array; Klarna and Edenred
// are separate merchant integrations surfaced alongside them. Labels are the same human-readable
// names used on the Payments tab (getMethodLabel), so they read as "Visa", "Mastercard", etc.
export type PaymentMethodRow = {
  key: string;
  label: string;
  enabled: boolean;
};

// Adyen ECOM `type` strings in display order (see SingleRestaurantPage/tabs/Payments/.../adyen/utils.ts).
const ADYEN_METHOD_ORDER: AdyenMethodType[] = [
  'visa',
  'mc',
  'amex',
  'applepay',
  'googlepay',
  'mobilepay',
  'ebanking_FI',
  'givex',
];

export const getPaymentMethodRows = (
  methods: PaymentMethodSettings[] | undefined,
  merchant: MerchantDto | undefined
): PaymentMethodRow[] => {
  const rows: PaymentMethodRow[] = [];

  for (const type of ADYEN_METHOD_ORDER) {
    const method = (methods ?? []).find((m) => m.type === type);
    if (!method) continue; // not configured for this shop → omit
    rows.push({ key: type, label: getMethodLabel(type), enabled: method.enabled });
  }

  // Klarna and Edenred are their own merchant integrations, not part of the Adyen method list.
  if (merchant?.klarnaMerchantData) {
    rows.push({
      key: 'klarna',
      label: 'Klarna',
      enabled: merchant.klarnaMerchantData.status === KlarnaMerchantStatus.Active,
    });
  }
  if (merchant?.edenredMerchantData) {
    rows.push({
      key: 'edenred',
      label: 'Edenred',
      enabled: merchant.edenredMerchantData.status === EdenredMerchantStatus.Active,
    });
  }

  // Enabled methods first, disabled after, each preserving definition order (stable).
  return rows.filter((r) => r.enabled).concat(rows.filter((r) => !r.enabled));
};

// Total number of products across a set of orders (sums line-item quantities). Used for the
// "products today" figure; falls back to the v1 order-items shape when V2 is absent.
export const countProductsInOrder = (singleOrder: OrderV2): number => {
  const items = singleOrder.shoppingCartPrice?.orderItemsV2 ?? singleOrder.shoppingCartPrice?.orderItems ?? [];
  return items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
};

export const countProductsInOrders = (orders: OrderV2[]): number =>
  orders.reduce((total, order) => total + countProductsInOrder(order), 0);
